import React, { useState, useEffect, useRef } from 'react';
import { testAudioRecording, testMicrophonePermission, checkBrowserCompatibility } from '../utils/audioTest';

const AudioTest: React.FC = () => {
  const [isTesting, setIsTesting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const runTests = async () => {
    setIsTesting(true);
    setError(null);
    
    try {
      const compatibility = checkBrowserCompatibility();
      const recordingSupported = testAudioRecording();
      const permissionGranted = await testMicrophonePermission();
      
      const overall = recordingSupported && permissionGranted && compatibility.mediaRecorder && compatibility.getUserMedia;
      
      if (overall) {
        setIsReady(true);
        startMicrophoneMonitoring();
      } else {
        setError('Audio recording is not supported or permission was denied');
      }
    } catch (error) {
      console.error('Test failed:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
    }
    
    setIsTesting(false);
  };

  const startMicrophoneMonitoring = async () => {
    try {
      // Get microphone stream with constraints
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      // Verify the stream has audio tracks
      const audioTracks = stream.getAudioTracks();
      console.log('Audio tracks found:', audioTracks.length);
      if (audioTracks.length > 0) {
        console.log('Audio track settings:', audioTracks[0].getSettings());
      }
      
      // Create audio context and analyser
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      // Configure analyser for better sensitivity
      analyser.fftSize = 512; // Increased for better resolution
      analyser.smoothingTimeConstant = 0.3; // Reduced for more responsive updates
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      console.log('Audio context created:', audioContext.state);
      console.log('Analyser connected, fftSize:', analyser.fftSize);
      
      // Resume audio context if it's suspended (required by some browsers)
      if (audioContext.state === 'suspended') {
        console.log('Audio context suspended, attempting to resume...');
        await audioContext.resume();
        console.log('Audio context resumed:', audioContext.state);
      }
      
      // Test if the analyser is working by getting some data immediately
      const testDataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(testDataArray);
      console.log('Test data array (should not be all zeros):', Array.from(testDataArray.slice(0, 10)));
      
      setIsMonitoring(true);
      console.log('Starting audio monitoring...');
      monitorAudioLevel();
      
    } catch (error) {
      console.error('Error starting microphone monitoring:', error);
      setError('Failed to access microphone');
    }
  };

  const monitorAudioLevel = () => {
    console.log('monitorAudioLevel called, analyserRef.current:', !!analyserRef.current);
    if (!analyserRef.current) {
      console.error('No analyser available for monitoring');
      return;
    }
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    let frameCount = 0;
    let lastDataSum = 0;
    let staticDataCount = 0;
    
    console.log('Starting audio level monitoring loop...');
    
    const updateLevel = () => {
      if (!analyserRef.current) {
        console.log('Stopping monitoring - no analyser');
        return;
      }
      
      // Don't check isMonitoring state here as it might cause issues
      // Just check if we should continue based on the refs
      
      frameCount++;
      
      // Log first few frames to see if the loop is running
      if (frameCount <= 5) {
        console.log(`Frame ${frameCount} - Monitoring active`);
      }
      
      // Use frequency data for audio level detection
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average and max from frequency data
      let sum = 0;
      let maxValue = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
        maxValue = Math.max(maxValue, dataArray[i]);
      }
      const average = sum / dataArray.length;
      
      // Check if data is changing (not static)
      if (Math.abs(sum - lastDataSum) < 1) {
        staticDataCount++;
      } else {
        staticDataCount = 0;
      }
      lastDataSum = sum;
      
      // Warn if data appears static
      if (staticDataCount > 60) { // 1 second of static data
        console.warn('WARNING: Audio data appears static - no audio input detected');
        staticDataCount = 0; // Reset to avoid spam
      }
      
      // Calculate level from frequency data with fixed sensitivity
      const level = Math.min(100, (average / 255) * 100 * 10);
      
      setAudioLevel(level);
      
      // Debug logging every 30 frames (about twice per second)
      if (frameCount % 30 === 0) {
        console.log(`Frame ${frameCount} - Freq: avg=${average.toFixed(2)}, max=${maxValue} | Level: ${level.toFixed(2)}%`);
        console.log('Freq data sample:', Array.from(dataArray.slice(0, 10)));
        
        // Check if all values are the same (indicates no audio)
        const uniqueFreqValues = new Set(dataArray);
        console.log('Unique freq values:', uniqueFreqValues.size);
      }
      
      // Log every frame for the first 2 seconds to see what's happening
      if (frameCount <= 120) {
        console.log(`Frame ${frameCount}: avg=${average.toFixed(2)}, max=${maxValue}, level=${level.toFixed(2)}%`);
      }
      
      // Also log when we detect any significant audio (reduced frequency)
      if (level > 5 && frameCount % 10 === 0) {
        console.log(`AUDIO DETECTED! Level: ${level.toFixed(2)}%, Average: ${average.toFixed(2)}, Max: ${maxValue}`);
      }
      
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    
    updateLevel();
  };

  const stopMonitoring = () => {
    setIsMonitoring(false);
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    analyserRef.current = null;
  };

  // Test recording functionality
  const startTestRecording = async () => {
    try {
      if (!streamRef.current) {
        setError('No microphone stream available. Please start microphone test first.');
        return;
      }

      const mediaRecorder = new MediaRecorder(streamRef.current);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        console.log('Recording completed. Blob size:', blob.size, 'bytes');
        setRecordedBlob(blob);
        setError(null);
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('Recording error:', event);
        setError('Recording failed. Please check your microphone.');
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();
      
      // Auto-stop after 5 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
          setIsRecording(false);
          setRecordingDuration(0);
        }
      }, 5000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording. Please check your microphone permissions.');
    }
  };

  const stopTestRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
    }
  };

  const playRecording = () => {
    if (!recordedBlob) return;
    
    try {
      const audioUrl = URL.createObjectURL(recordedBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl); // Clean up the URL
      };
      audio.onerror = () => {
        setIsPlaying(false);
        setError('Failed to play recording. The audio format may not be supported.');
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.play();
    } catch (error) {
      console.error('Error playing recording:', error);
      setError('Failed to play recording. Please try again.');
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Update recording duration
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRecording && recordingStartTimeRef.current) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current!) / 1000);
        setRecordingDuration(elapsed);
      }, 100);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMonitoring();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="audio-test bg-white shadow rounded-lg p-6 mb-6">
      <style>{`
        .audio-test .slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }
        
        .audio-test .slider::-webkit-slider-track {
          background: #e5e7eb;
          height: 8px;
          border-radius: 4px;
        }
        
        .audio-test .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          background: #3b82f6;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          cursor: pointer;
        }
        
        .audio-test .slider::-moz-range-track {
          background: #e5e7eb;
          height: 8px;
          border-radius: 4px;
          border: none;
        }
        
        .audio-test .slider::-moz-range-thumb {
          background: #3b82f6;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          cursor: pointer;
          border: none;
        }
      `}</style>
      
      <h3 className="text-lg font-bold text-gray-900 mb-4">Audio Recording Test</h3>
      
      {!isReady ? (
        <button
          onClick={runTests}
          disabled={isTesting}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md font-medium"
        >
          {isTesting ? 'Testing...' : 'Test Audio Recording'}
        </button>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <span className="text-green-600 font-medium">✅ Audio recording is ready!</span>
            {isMonitoring && (
              <button
                onClick={stopMonitoring}
                className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
              >
                Stop Test
              </button>
            )}
          </div>
          
          {!isMonitoring && (
            <button
              onClick={startMicrophoneMonitoring}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Start Microphone Test
            </button>
          )}
          
          {isMonitoring && (
            <div className="space-y-4">
              {/* Audio Level Indicator */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio Level: {Math.round(audioLevel)}%
                </label>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-100 ${
                      audioLevel > 50 ? 'bg-red-500' : 
                      audioLevel > 25 ? 'bg-yellow-500' : 
                      audioLevel > 10 ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                    style={{ width: `${Math.max(2, audioLevel)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Quiet</span>
                  <span>Good</span>
                  <span>Loud</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isMonitoring ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                <p className="text-sm text-gray-600">
                  {isMonitoring ? 'Monitoring active - Speak into your microphone' : 'Monitoring stopped'}
                </p>
              </div>
              
              <div className="text-xs text-gray-500">
                Current level: {Math.round(audioLevel)}% | 
                {audioLevel > 5 ? ' 🎤 Audio detected!' : ' 🔇 No audio detected'}
              </div>
              
              {/* Test Recording Section */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Test Recording</h4>
                <div className="space-y-3">
                  {/* Recording Controls */}
                  <div className="flex items-center space-x-3">
                    {!isRecording ? (
                      <button
                        onClick={startTestRecording}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium text-sm"
                      >
                        Start 5-Second Test Recording
                      </button>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={stopTestRecording}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium text-sm"
                        >
                          Stop Recording
                        </button>
                        <div className="text-sm text-gray-600">
                          Recording: {recordingDuration}s / 5s
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Playback Controls */}
                  {recordedBlob && (
                    <div className="flex items-center space-x-3">
                      {!isPlaying ? (
                        <button
                          onClick={playRecording}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium text-sm"
                        >
                          ▶️ Play Recording
                        </button>
                      ) : (
                        <button
                          onClick={stopPlayback}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md font-medium text-sm"
                        >
                          ⏹️ Stop Playback
                        </button>
                      )}
                      <div className="text-sm text-gray-600">
                        {isPlaying ? 'Playing...' : `Recording ready (${Math.round(recordedBlob.size / 1024)}KB)`}
                      </div>
                    </div>
                  )}
                  
                  {isRecording && (
                    <div className="text-xs text-gray-500">
                      Speak into your microphone to test recording quality
                    </div>
                  )}
                  
                  {recordedBlob && !isPlaying && (
                    <div className="text-xs text-gray-500">
                      Click "Play Recording" to hear your test recording
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 mb-3">
                  <strong>Note:</strong> This shows your microphone input level. To adjust the actual recording volume:
                </p>
                <div className="space-y-2 text-sm text-blue-700">
                  <div>
                    <strong>Windows:</strong> Right-click speaker icon → "Open Sound settings" → "Sound Control Panel" → "Recording" tab
                  </div>
                  <div>
                    <strong>Mac:</strong> System Preferences → Sound → Input tab
                  </div>
                  <div>
                    <strong>Browser:</strong> Check microphone permissions in your browser settings
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">❌ {error}</p>
        </div>
      )}
    </div>
  );
};

export default AudioTest;


